import { Trans, useLingui } from '@lingui/react/macro';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import {
  TEMPLATE_NAME_CONFLICT_MESSAGE,
  isTemplateNameConflict,
  normalizeTemplateName,
} from 'modules/manager/domain/template-name-conflict';
import { TEMPLATE_RESOLUTIONS, resolveTemplateResolution } from 'modules/manager/domain/template-resolutions';
import { useCallback, useMemo, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerTemplateForm, {
  DEFAULT_RESOLUTION_OPTION,
  MediaManagerTemplateFormValues,
  buildInitialSubtype,
  getDefaultTemplateTypeOption,
  getResolutionOption,
  getTemplateTypeOption,
} from 'modules/manager/ui/components/forms/media-manager-template-form';

interface Args {
  templates: MediaManagerTemplate[];
  onUpdated: () => void;
}

function useEditTemplateModal({ templates, onUpdated }: Args) {
  const { t } = useLingui();
  const [template, setTemplate] = useState<MediaManagerTemplate | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((next: MediaManagerTemplate) => {
    setError(null);
    setTemplate(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  // Keeping its own name is not a conflict — compared the way the DB compares.
  const existingNames = useMemo(
    () =>
      templates
        .map((t) => t.name.trim())
        .filter((name) => normalizeTemplateName(name) !== normalizeTemplateName(template?.name ?? '')),
    [templates, template],
  );

  const onSubmit = useCallback(
    async (values: MediaManagerTemplateFormValues) => {
      if (!template) return;
      setIsLoading(true);
      setError(null);
      try {
        const resolution = TEMPLATE_RESOLUTIONS[values.resolution.value];
        await mediaManagerRepository.updateTemplate({
          id: template.id,
          name: values.name.trim(),
          width: resolution.width,
          height: resolution.height,
          templateType: values.templateType.value,
          templateSubtypes: values.subtype ? [values.subtype.value] : [],
        });
        onUpdated();
        close();
      } catch (err) {
        console.error('Failed to update media manager template:', err);
        setError(isTemplateNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось сохранить шаблон, попробуйте ещё раз`);
      } finally {
        setIsLoading(false);
      }
    },
    [template, onUpdated, close, t],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type="title-3" className="mb-5">
          <Trans>Редактировать шаблон</Trans>
        </UDText>
        <MediaManagerTemplateForm
          initialValues={{
            name: template?.name ?? '',
            resolution: template
              ? getResolutionOption(resolveTemplateResolution(template.width, template.height).key)
              : DEFAULT_RESOLUTION_OPTION,
            templateType: template ? getTemplateTypeOption(template.templateType) : getDefaultTemplateTypeOption(),
            subtype: template ? buildInitialSubtype(template.templateType, template.templateSubtypes) : null,
          }}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          submitLabel={t`Сохранить`}
          onSubmit={onSubmit}
          enableReinitialize
        />
      </Modal>
    ),
  };
}

export default useEditTemplateModal;
